import { computed, ref, watch } from 'vue';
import it from './it';
import en from './en';

export type Locale = 'it' | 'en';
export type MessageKeys = keyof typeof it;

const messages: Record<Locale, Record<string, unknown>> = { it, en };

// Chiave di storage per la preferenza lingua (NO localStorage in artifact, ma OK qui perché
// questo non è un artifact: è un'app vera che gira nel suo ambiente.)
export const STORAGE_KEY = 'swipewish.locale';

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'it' || saved === 'en') return saved;
  } catch { /* SSR / test */ }
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'it';
  return nav.startsWith('it') ? 'it' : 'en';
}

export const currentLocale = ref<Locale>(detectLocale());

watch(currentLocale, (v) => {
  try { localStorage.setItem(STORAGE_KEY, v); } catch { /* noop */ }
  document.documentElement.lang = v;
});

/**
 * Composable principale per l'internazionalizzazione.
 *
 * Esempio d'uso:
 * ```ts
 * const { t, locale, setLocale } = useI18n();
 * t('auth_sign_in')        // → "Accedi" (it) / "Sign In" (en)
 * t('swipe_empty_desc', { radius: 100 })  // interpolazione
 * ```
 */
export function useI18n() {
  const locale = computed({
    get: () => currentLocale.value,
    set: (v: Locale) => { currentLocale.value = v; },
  });

  function setLocale(l: Locale) {
    currentLocale.value = l;
  }

  /**
   * Traduce la chiave. Supporta interpolazione con `{variabile}`.
   * Se la chiave non esiste, ritorna la chiave stessa.
   */
  function t(key: string, params?: Record<string, string | number>): string {
    const dict = messages[currentLocale.value] as Record<string, unknown>;
    let raw = dict[key];
    if (raw === undefined) {
      // Fallback sull'altra lingua
      const fallback = currentLocale.value === 'it' ? messages.en : messages.it;
      raw = (fallback as Record<string, unknown>)[key];
    }
    if (raw === undefined) return key;
    if (typeof raw !== 'string') return String(raw);
    if (!params) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? `{${k}}`));
  }

  /**
   * Ritorna un array tradotto (per le sub_features, per esempio).
   */
  function ta(key: string): string[] {
    const dict = messages[currentLocale.value] as Record<string, unknown>;
    const val = dict[key] ?? (messages[currentLocale.value === 'it' ? 'en' : 'it'] as Record<string, unknown>)[key];
    return Array.isArray(val) ? val : [];
  }

  return { locale, setLocale, t, ta };
}
